import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import useFetch from '../../hooks/useFetch';
import { GenerateSHGCode } from '../../components/GenerateThink5whysCode';
import { logout } from "../../redux/actions/authActions";

const SHGRegister = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData, { loading }] = useFetch();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isLoggedIn = authState?.isLoggedIn || false;
  const userId = authState.user._id;
  const userName = authState?.user?.name || "User";
  const userThink5WhysCode = authState?.user?.think5whyscode || ""; 
  const [isCreated, setIsCreated] = useState(false);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/sheearns");
  };

  const emptyForm = {
    shgCode: GenerateSHGCode(),
    shgName: "",
    shgLeaderName: userName,
    shgLeaderThink5whysCode: userThink5WhysCode,
    shgPlace: "",
    shgAddress: "",
    shgState: "",
    shgPIN: "",
    shgMobile: "",
    shgEmail: "",
    shgRegistrationNumer: "",
    shgDetails: "",
    shgMicroPage: "",
  };

  const [form, setForm] = useState(emptyForm);
  const [shgList, setShgList] = useState([]);
  const [editId, setEditId] = useState(null);
  const [message, setMessage] = useState("");

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = () => {
    if (!form.shgName || !form.shgPlace || !form.shgAddress || !form.shgState || !form.shgPIN || !form.shgEmail || !form.shgMobile) {
      setMessage("SHG Name, Place, SHG Address, SHG State, PIN Code, Email & Mobile Number are required.");
      return;
    }

    const pinRegex = /^[0-9]{6}$/;
    if (!pinRegex.test(form.shgPIN)) {
      setMessage("PIN Code must be exactly 6 digits.");
      return;
    }

    const mobileRegex = /^\+?[0-9]{10,}$/;
    if (!mobileRegex.test(form.shgMobile)) {
      setMessage("Mobile Number must contain only digits (optional leading +) and be at least 10 digits.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (form.shgEmail && !emailRegex.test(form.shgEmail)) {
      setMessage("Enter a valid Email ID.");
      return;
    }

    const url = editId ? "/shgmaster/update" : "/shgmaster/create";
    const payload = editId
      ? { shgId: editId, SHGData: form }
      : { ...form, createdBy: userId };

    const config = {
      url,
      method: "post",
      headers: { Authorization: authState.token },
      data: payload,
    };

    fetchData(config)
      .then((res) => {
        if (res?.status) {
          setMessage(editId ? "SHG updated successfully" : "SHG created!");
          setForm(emptyForm);
          setEditId(null);
          setIsCreated(true);
          loadSHGs();
        } else {
          setMessage("Failed. Try again.");
        }
      })
      .catch(() => setMessage("Error saving SHG"));
  };

  const loadSHGs = useCallback(() => {
    const config = {
      url: "/shgmaster/myshg",
      method: "post",
      headers: { Authorization: authState.token },
      data: { createdBy: userId },
    };

    fetchData(config)
      .then((res) => {
        if (res?.status) {
          setShgList(res.shgList);
        }
      })
      .catch(() => setMessage("Error loading SHGs"));
  }, [userId, authState.token]);

  useEffect(() => {
    if (userId) loadSHGs();
  }, [userId, loadSHGs]);

  const handleEdit = (id) => {
    const config = {
      url: "/shgmaster/details",
      method: "post",
      headers: { Authorization: authState.token },
      data: { shgId: id },
    };

    fetchData(config).then((res) => {
      if (res?.status && res.shg) {
        setForm(res.shg);
        setEditId(id);
      }
    });
  };

  const handleDelete = (id) => {
    const config = {
      url: "/shgmaster/update",
      method: "post",
      headers: { Authorization: authState.token },
      data: { shgId: id, SHGData: { isDeleted: true, isActive: false } },
    };

    fetchData(config).then((res) => {
      if (res?.status) {
        setMessage("SHG deleted");
        loadSHGs();
      }
    });
  };

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login"); 
      return;
    }
  }, [isLoggedIn, navigate]);

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="flex flex-col bg-[#1a0b1e] text-pink-100 font-sans">
        <header className="sticky top-0 z-50 bg-[#2a0f2f] border-b border-pink-800 px-4 py-2 flex items-center justify-between shadow-lg">
          <div className="flex flex-col">
            <div className="text-xl lg:text-2xl font-bold text-pink-400">Think5Whys SHG</div>
            <div className="text-[10px] lg:text-lg text-yellow-200">
               Registration Form
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <div className="text-xs text-left text-pink-300">{userName}</div>
                <button
                  onClick={handleLogout}
                  className="p-2 mt-2 rounded-md bg-rose-600 hover:bg-rose-700 transition"
                  title="Logout"
                >
                  ⏻
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  className="px-3 py-1 rounded-full border border-pink-700 text-sm text-pink-200 hover:bg-pink-900"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate("/signup")}
                  className="px-3 py-1 rounded-full bg-pink-500 text-white text-sm hover:bg-pink-400"
                >
                  Register
                </button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen((s) => !s)}
            className="md:hidden w-6 h-6 flex items-center justify-center text-xl text-pink-300 bg-pink-500"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? "×" : "☰"}
          </button>
        </header>

        {/* Mobile menu content */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#2a0f2f] border-b border-pink-800 px-4 py-2 animate-fadeIn">
            {isLoggedIn ? (
              <div className="flex flex-row items-center justify-center gap-3">
                <span className="text-pink-300 font-medium whitespace-nowrap h-7">
                  Welcome, {userName}
                </span>
                <button
                  onClick={handleLogout}
                  className="rounded-md bg-rose-600 hover:bg-rose-700 w-7 h-7 flex items-center justify-center text-white"
                  title="Logout"
                >
                  ⏻
                </button>
              </div>
            ) : (
              <div className="flex flex-row items-center justify-center gap-6">
                <Link to="/login" className="text-pink-300 hover:text-yellow-300">
                  Login
                </Link>
                <span className="text-pink-500">|</span>
                <Link to="/signup" className="text-pink-300 hover:text-yellow-300">
                  Register
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      <section className="bg-black py-8 px-4">
        <div className="p-4 text-white bg-black min-h-screen">
          <h1 className="text-xl font-bold mb-4 text-pink-300">
            Create / Update / Delete
          </h1>

          <div className="bg-gray-800 rounded-lg p-4 border border-pink-700 mb-6">
            <h2 className="text-lg font-bold text-pink-400 mb-3">
              {editId ? "Edit SHG" : "Create New SHG"}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.keys(emptyForm).map((key) => {
                const isTextArea = ["shgDetails", "shgMicroPage"].includes(key);

                const disabledFields = [
                  "shgCode",
                  "shgLeaderName",
                  "shgLeaderThink5whysCode"
                ];

                const commonProps = {
                  key,
                  name: key,
                  value: form[key],
                  onChange: handleChange,
                  placeholder: key,
                  disabled: disabledFields.includes(key),
                  className:
                    "p-2 rounded bg-gray-700 border border-gray-600 disabled:bg-gray-800 disabled:text-gray-400",
                };

                if (key === "shgMobile") {
                  commonProps.type = "tel";
                  commonProps.maxLength = 10;
                  commonProps.pattern = "[0-9]{10}";
                  commonProps.inputMode = "numeric";
                }

                if (key === "shgPIN") {
                  commonProps.type = "text";
                  commonProps.maxLength = 6;
                  commonProps.pattern = "[0-9]{6}";
                  commonProps.inputMode = "numeric";
                }

                if (key === "shgEmail") {
                  commonProps.type = "email";
                }

                return isTextArea ? (
                  <textarea
                    {...commonProps}
                    className={`${commonProps.className} h-28`}
                  />
                ) : (
                  <input {...commonProps} />
                );
              })}
            </div>

            {message && (
              <p className="text-yellow-300 mb-3 text-center">{message}</p>
            )}
            <div className="mt-4 flex gap-3">
              <button
                onClick={handleSave}
                disabled={isCreated}
                className="bg-pink-600 px-4 py-2 rounded"
              >
                {editId ? "Update SHG" : "Create SHG"}
              </button>

              {editId && (
                <button
                  onClick={() => {
                    setForm(emptyForm);
                    setEditId(null);
                  }}
                  className="bg-gray-500 px-4 py-2 rounded"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
            <h2 className="text-lg font-bold text-purple-400 mb-3">
              My SHG List
            </h2>

            {shgList.length === 0 ? (
              <p className="text-gray-400">No SHGs found.</p>
            ) : (
              <div className="space-y-3">
                {shgList.map((shg) => (
                  <div
                    key={shg._id}
                    className="p-3 bg-gray-800 rounded border border-gray-700"
                  >
                    <div className="font-semibold text-yellow-300">
                      {shg.shgName} ({shg.shgCode})
                    </div>
                    <div className="flex gap-3">
                    <button
                      onClick={() => handleEdit(shg._id)}
                      className="w-8 h-8 flex items-center justify-center 
                                bg-blue-600 rounded-full hover:bg-blue-500"
                      title="Edit SHG"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(shg._id)}
                      className="w-8 h-8 flex items-center justify-center 
                                bg-red-600 rounded-full hover:bg-red-500"
                      title="Delete SHG"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                    <div className="font-semibold text-yellow-300">
                      Place: {shg.shgPlace} 
                    </div>
                    <div className="text-gray-400 text-sm">
                      Leader: {shg.shgLeaderName}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <footer className="bg-[#2a0f2f] border-t border-pink-800 text-pink-300 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs lg:text-sm">
            © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
          </div>
          <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
            <Link to="/about-us" className="text-white hover:text-pink-400">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-pink-400">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-pink-400">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-pink-400">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SHGRegister;